import axios from "axios";
import store from "../store";
import { useToast } from "@/composables/useToast";

const { add: addToast } = useToast();

const api = axios.create({
    baseURL: process.env.NODE_ENV === 'production' ? (process.env.VUE_APP_API_URL || "/api") : "http://localhost:5000/api",
});

// Attach token
api.interceptors.request.use(config => {
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

/**
 * Translates an Axios error into a clear, user-friendly Arabic message.
 * Returns: { message: string, type: ToastType }
 */
function getErrorInfo(err) {
    // ─── No response at all (network/offline) ───────────────────────────────
    if (!err.response) {
        if (!navigator.onLine) {
            return {
                message: 'لا يوجد اتصال بالإنترنت، تأكد من اتصالك ثم حاول مجدداً.',
                type: 'network'
            };
        }
        return {
            message: 'تعذر الوصول إلى الخادم. تأكد من اتصالك بالإنترنت أو حاول لاحقاً.',
            type: 'network'
        };
    }

    const status = err.response.status;
    const data = err.response.data;
    const url = err.config?.url || '';
    const isAuthRoute = url.includes('/auth/');

    // ─── Backend sent a clear Arabic message ───────────────────────────────
    const backendMsg = data?.message;

    switch (status) {
        // ── 400 Bad Request ─────────────────────────────────────────────────
        case 400: {
            if (data?.errors && Array.isArray(data.errors)) {
                return {
                    message: data.errors.map(e => e.msg || e).join(' | '),
                    type: 'warning'
                };
            }
            return {
                message: backendMsg || 'البيانات المُدخلة غير صحيحة، يرجى مراجعتها والمحاولة مجدداً.',
                type: 'warning'
            };
        }

        // ── 401 Unauthorized ────────────────────────────────────────────────
        case 401: {
            if (isAuthRoute) {
                return {
                    message: backendMsg || 'كلمة المرور غير صحيحة. يرجى المحاولة مجدداً.',
                    type: 'error'
                };
            }
            store.dispatch('auth/logout');
            return {
                message: 'انتهت جلستك، يرجى تسجيل الدخول مجدداً.',
                type: 'warning'
            };
        }

        // ── 403 Forbidden ───────────────────────────────────────────────────
        case 403: {
            if (isAuthRoute) {
                store.dispatch('auth/logout');
                return {
                    message: 'انتهت جلستك أو غير مصرح لك، يرجى تسجيل الدخول مجدداً.',
                    type: 'warning'
                };
            }
            return {
                message: backendMsg || 'ليس لديك صلاحية للقيام بهذه العملية.',
                type: 'error'
            };
        }

        // ── 404 Not Found ───────────────────────────────────────────────────
        case 404: {
            if (isAuthRoute) {
                return {
                    message: backendMsg || 'البريد الإلكتروني غير مسجل لدينا. تحقق من البريد أو سجّل حساباً جديداً.',
                    type: 'warning'
                };
            }
            if (backendMsg === 'المستخدم غير موجود') {
                store.dispatch('auth/logout');
                return {
                    message: 'انتهت صلاحية الجلسة، يرجى تسجيل الدخول مجدداً.',
                    type: 'warning'
                };
            }
            return {
                message: backendMsg || 'العنصر المطلوب غير موجود.',
                type: 'warning'
            };
        }

        // ── 409 Conflict ────────────────────────────────────────────────────
        case 409: {
            return {
                message: backendMsg || 'البيانات موجودة بالفعل ولا يمكن تكرارها.',
                type: 'warning'
            };
        }

        // ── 429 Too Many Requests ───────────────────────────────────────────
        case 429: {
            return {
                message: 'لقد أجريت محاولات كثيرة جداً، يرجى الانتظار قليلاً ثم المحاولة مجدداً.',
                type: 'warning'
            };
        }

        // ── 500+ Server Error ───────────────────────────────────────────────
        default: {
            if (status >= 500) {
                // If backend already sent a descriptive Arabic message, use it
                if (backendMsg && backendMsg.length > 5) {
                    return { message: backendMsg, type: 'error' };
                }
                return {
                    message: 'حدث خطأ في الخادم، نحن نعمل على حله. يرجى المحاولة مجدداً بعد قليل.',
                    type: 'error'
                };
            }
            return {
                message: backendMsg || 'حدث خطأ غير متوقع. يرجى المحاولة مجدداً.',
                type: 'error'
            };
        }
    }
}

// Global response handler for errors
api.interceptors.response.use(
    res => res,
    err => {
        // Ignore cancelled requests
        if (axios.isCancel(err)) return Promise.reject(err);

        // If the caller passes { params: { _silent: true } } or config._silent, skip toast
        const isSilent = err.config?._silent === true;

        if (!isSilent) {
            const { message, type } = getErrorInfo(err);
            addToast(message, type);
        }

        return Promise.reject(err);
    }
);

export default api;
